import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminUpdateStorePackOrderLimitConfigMutation,
  AdminUpdateStorePackOrderLimitConfigMutationVariables,
  PackOrderLimitConfigStoreListQuery,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const PACK_ORDER_LIMIT_CONFIG_STORE_LIST = gql(`
  query PackOrderLimitConfigStoreList {
    getStoreList {
      ok
      error
      result {
        id
        title
        address
        isPackOrderLimitConfigEnabled
        pack {
          id
          maxOrderQuantity
        }
      }
    }
  }
`);

const ADMIN_UPDATE_STORE_PACK_ORDER_LIMIT_CONFIG = gql(`
  mutation AdminUpdateStorePackOrderLimitConfig(
    $storeId: Int!
    $enabled: Boolean!
    $maxOrderQuantity: Int
  ) {
    adminUpdateStorePackOrderLimitConfig(
      storeId: $storeId
      enabled: $enabled
      maxOrderQuantity: $maxOrderQuantity
    ) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  PackOrderLimitConfigStoreListQuery["getStoreList"]
>["result"][number];

const LIMIT_OPTIONS = [
  { value: "none", label: "없음" },
  { value: "1", label: "1개" },
  { value: "2", label: "2개" },
  { value: "3", label: "3개" },
  { value: "4", label: "4개" },
] as const;

function formatLimit(value: number | null | undefined): string {
  if (value == null) {
    return "없음";
  }
  return `${value}개`;
}

function formatEnabled(value: boolean | null | undefined): string {
  return value ? "사용" : "미사용";
}

export default function AdminPackOrderLimitConfig() {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [limitValue, setLimitValue] = useState<string>("none");
  const [searchKeyword, setSearchKeyword] = useState("");

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } =
    useQuery<PackOrderLimitConfigStoreListQuery>(
      PACK_ORDER_LIMIT_CONFIG_STORE_LIST,
    );
  const [adminUpdateStorePackOrderLimitConfig, { loading: updating }] =
    useMutation<
      AdminUpdateStorePackOrderLimitConfigMutation,
      AdminUpdateStorePackOrderLimitConfigMutationVariables
    >(ADMIN_UPDATE_STORE_PACK_ORDER_LIMIT_CONFIG, {
      onCompleted: (res) => {
        if (res.adminUpdateStorePackOrderLimitConfig.ok) {
          notify("예약팩 주문 수량 제한 설정이 반영되었습니다.");
          refetch();
        } else {
          notifyError(
            res.adminUpdateStorePackOrderLimitConfig.error ??
              "설정에 실패했습니다.",
          );
        }
      },
      onError: (e) => {
        notifyError(e.message);
      },
    });

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const keyword = searchKeyword.trim().toLowerCase();
  const list = keyword
    ? fullList.filter((store) => store.title.toLowerCase().includes(keyword))
    : fullList;

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
    setEnabled(store.isPackOrderLimitConfigEnabled ?? false);
    setLimitValue(
      store.pack?.maxOrderQuantity != null
        ? String(store.pack.maxOrderQuantity)
        : "none",
    );
  };

  const handleApply = () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }

    adminUpdateStorePackOrderLimitConfig({
      variables: {
        storeId: selectedStore.id,
        enabled,
        maxOrderQuantity:
          enabled && limitValue !== "none" ? Number(limitValue) : null,
      },
    });
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }

  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="예약팩 주문 수량 제한 설정"
      description="매장별로 일일 최대 주문 팩 수량 설정 기능 오픈 여부와 제한 개수를 관리합니다."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명으로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((s) => ({
              id: s.id,
              title: s.title,
              address: s.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (full) handleSelectStore(full);
            }}
            showImage={false}
            extraHeaders={
              <>
                <th className="px-4 py-2 font-medium text-gray-700">기능</th>
                <th className="px-4 py-2 font-medium text-gray-700">
                  제한 개수
                </th>
              </>
            }
            renderExtraCells={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <>
                  <td className="px-4 py-3 text-gray-600">
                    {formatEnabled(full.isPackOrderLimitConfigEnabled)}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {full.isPackOrderLimitConfigEnabled
                      ? formatLimit(full.pack?.maxOrderQuantity)
                      : "—"}
                  </td>
                </>
              );
            }}
            renderExtraCard={(store) => {
              const full = list.find((x) => x.id === store.id);
              if (!full) return null;
              return (
                <div className="mt-2 space-y-1 text-xs text-gray-600">
                  <p>
                    기능: {formatEnabled(full.isPackOrderLimitConfigEnabled)}
                  </p>
                  <p>
                    제한 개수:{" "}
                    {full.isPackOrderLimitConfigEnabled
                      ? formatLimit(full.pack?.maxOrderQuantity)
                      : "—"}
                  </p>
                </div>
              );
            }}
            emptyText={
              keyword ? "검색 결과가 없습니다." : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "주문 수량 제한 설정",
        children: selectedStore ? (
          <>
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>
            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              <div className="font-medium text-gray-700 mb-2">현재 설정</div>
              <div>
                기능:{" "}
                {formatEnabled(selectedStore.isPackOrderLimitConfigEnabled)}
              </div>
              <div>
                제한 개수:{" "}
                {selectedStore.isPackOrderLimitConfigEnabled
                  ? formatLimit(selectedStore.pack?.maxOrderQuantity)
                  : "—"}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                기능 사용 여부
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="enabled"
                    checked={enabled}
                    onChange={() => setEnabled(true)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">사용</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="enabled"
                    checked={!enabled}
                    onChange={() => setEnabled(false)}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">미사용</span>
                </label>
              </div>
            </div>
            {enabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  일일 최대 주문 팩 수량
                </label>
                <select
                  value={limitValue}
                  onChange={(e) => setLimitValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  {LIMIT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              type="button"
              disabled={updating}
              onClick={handleApply}
              className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? "저장 중..." : "적용"}
            </button>
          </>
        ) : null,
      }}
    />
  );
}
